import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { ReactNode } from 'react';
import { cn } from 'utils/cn';
import LabelledData from '../LabelledData';
import { GroupedTimecards } from '../TimecardGroup';

/**
 * Stands in for each figure until the group's own totals arrive. The loaded rows are a fraction of the
 * group, so summing them would state a number that then changes — an em dash says "not known yet", which
 * is what it is.
 */
const PENDING_FIGURE = '\u2014';

type Props = {
    title: string;
    /** Second line of context beside the title — the group's client, or the author's office. */
    subtitle?: string;
    group: GroupedTimecards;
    /** No totals for this group yet, so `group`'s sums cover only the rows the list has loaded. */
    totalsPending?: boolean;
};

/**
 * Fee sums in the order the ribbon lists them: the base currency first, then the rest alphabetically,
 * so the number a reader is looking for sits in the same place in every group.
 */
export function orderFeeCurrencies(feesByCurrency: Map<string, number>, baseCurrency: string): string[] {
    return [...feesByCurrency.keys()].toSorted((left, right) => {
        if (left === baseCurrency) return -1;
        if (right === baseCurrency) return 1;
        return left.localeCompare(right);
    });
}

/** Half a cent: below it the two figures round to the same displayed amount. */
const BASE_CURRENCY_TOTAL_EPSILON = 0.005;

/**
 * Whether the ribbon appends the base-currency grand total in brackets — true whenever that figure is
 * not already on display, which is NOT the same test as "some currency is not the base one".
 *
 * The per-currency sums add up the rows' OWN amounts, while the base total is what each row converted
 * to, and for an invoiced timecard that is the rate frozen at invoicing. So a group priced entirely in
 * the base currency can still have two different figures, and keying the bracket on the currency alone
 * would hide the difference rather than show it.
 */
export function shouldShowBaseCurrencyTotal(group: GroupedTimecards, currencies: string[]): boolean {
    if (currencies.length === 0) {
        return false;
    }
    const baseCurrency = group.baseCurrency ?? '';
    if (currencies.length > 1 || currencies[0] !== baseCurrency) {
        return true;
    }
    const shown = group.feesByCurrency.get(baseCurrency) ?? 0;
    return Math.abs(shown - group.totalAmountInBaseCurrency) >= BASE_CURRENCY_TOTAL_EPSILON;
}

/**
 * The strip above a group of rows: what the group is, and its hours and fees over every timecard the
 * filter matches, not over the ones scrolled into view (see `TimecardListWithFilters`).
 *
 * Fees are listed PER CURRENCY, since a group can hold timecards priced in several and nothing is
 * converted here; the base-currency grand total follows in brackets when it adds something, which is
 * the one figure the per-currency sums cannot be added up to.
 */
export function TimecardGroupRibbon({ title, subtitle, group, totalsPending = false }: Props): JSX.Element {
    const baseCurrency = group.baseCurrency ?? '';
    const currencies = orderFeeCurrencies(group.feesByCurrency, baseCurrency);
    const showBaseCurrencyTotal = shouldShowBaseCurrencyTotal(group, currencies);

    return (
        <div className="bg-grey-50 flex w-full items-baseline gap-4 px-4 py-[9px]">
            <div className="flex min-w-0 flex-1 items-baseline gap-2">
                <span className="text-subtitle2 font-semibold whitespace-nowrap">{title}</span>
                {subtitle ? <span className="text-caption text-fg-secondary truncate">{subtitle}</span> : null}
            </div>
            {/* Dimmed rather than collapsed while pending, so the ribbon keeps its height and the rows
                below it do not shift when the figures arrive. */}
            <div className={cn('flex flex-none gap-2', totalsPending && 'opacity-30')}>
                <LabelledData label="timecards.list.total-time" className="font-semibold">
                    {totalsPending ? PENDING_FIGURE : <HoursDisplay value={group.loggedTimeSumInHours} />}
                </LabelledData>
                <LabelledData label="timecards.list.billable-time" className="font-semibold">
                    {totalsPending ? PENDING_FIGURE : <HoursDisplay value={group.billableTimeSumInHours} />}
                </LabelledData>
                <LabelledData label="timecards.list.total-amount" className="font-semibold">
                    {/* A group with no billable fee at all still shows a zero in the base currency,
                            rather than an empty slot the reader has to interpret. */}
                    {totalsPending ? (
                        PENDING_FIGURE
                    ) : currencies.length === 0 ? (
                        <MoneyDisplay amount={0} currency={baseCurrency} />
                    ) : (
                        currencies.map<ReactNode>((currency, index) => (
                            <span key={currency} className="whitespace-nowrap">
                                {index > 0 ? ', ' : null}
                                <MoneyDisplay amount={group.feesByCurrency.get(currency) ?? 0} currency={currency} />
                            </span>
                        ))
                    )}
                    {!totalsPending && showBaseCurrencyTotal ? (
                        <span className="text-grey-600 ml-[0.5ch] whitespace-nowrap">
                            (<MoneyDisplay amount={group.totalAmountInBaseCurrency} currency={baseCurrency} />)
                        </span>
                    ) : null}
                </LabelledData>
            </div>
        </div>
    );
}
